import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { ai, AI_PROXY_CHAT_MODEL } from '../services/geminiService';
import { Mail, Plus, Trash2, Edit2, Check, Loader2, Wand2, ArrowLeft, PenTool } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';

interface EmailTemplate {
    id: string;
    name: string;
    subject: string;
    body: string;
    pipelineStatus?: string;
    pipelineStatuses?: string[];
    creatorId?: string;
    visibility?: 'company' | 'private' | 'specific';
    sharedWithIds?: string[];
}

interface EmailTemplatesSettingsTabProps {
    clientId: string;
    userId: string;
}

export const EmailTemplatesSettingsTab: React.FC<EmailTemplatesSettingsTabProps> = ({ clientId, userId }) => {
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [teamMembers, setTeamMembers] = useState<{ id: string, name: string }[]>([]);
    const [emailSignature, setEmailSignature] = useState('');
    const [isSavingSignature, setIsSavingSignature] = useState(false);
    const [showHtml, setShowHtml] = useState(false);
    const [sharedMailboxes, setSharedMailboxes] = useState('');
    const [defaultSharedMailbox, setDefaultSharedMailbox] = useState('');

    const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
    const [aiPrompt, setAiPrompt] = useState('');

    useEffect(() => {
        loadTemplates();
        fetchTeamMembers();
    }, [clientId]);

    const fetchTeamMembers = async () => {
        try {
            const { data } = await supabase.from('profiles').select('id, name').eq('client_id', clientId);
            if (data) setTeamMembers(data);
        } catch(e) {}
    };

    const loadTemplates = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase.from('clients').select('data').eq('id', clientId).single();
            if (error) throw error;
            if (data?.data?.email_templates) {
                setTemplates(data.data.email_templates);
            }
            if (data?.data?.user_signatures && data.data.user_signatures[userId]) {
                setEmailSignature(data.data.user_signatures[userId]);
            } else if (data?.data?.email_signature) {
                setEmailSignature(data.data.email_signature);
            }
            if (data?.data?.shared_mailboxes) {
                setSharedMailboxes(data.data.shared_mailboxes);
            }
            if (data?.data?.default_shared_mailbox) {
                setDefaultSharedMailbox(data.data.default_shared_mailbox);
            }
        } catch (e) {
            console.error("Fout bij laden e-mail templates", e);
        } finally {
            setIsLoading(false);
        }
    };

    const saveTemplatesToDb = async (newTemplates: EmailTemplate[]) => {
        setIsSaving(true);
        try {
            const { data: clientData } = await supabase.from('clients').select('data').eq('id', clientId).single();
            const currentData = clientData?.data || {};
            currentData.email_templates = newTemplates;

            const { error } = await supabase.from('clients').update({ data: currentData }).eq('id', clientId);
            if (error) throw error;
            setTemplates(newTemplates);
            setEditingTemplate(null);
        } catch (e) {
            console.error("Fout bij opslaan", e);
            alert("Kon templates niet opslaan.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveSignature = async () => {
        setIsSavingSignature(true);
        try {
            const { data: clientData } = await supabase.from('clients').select('data').eq('id', clientId).single();
            const currentData = clientData?.data || {};
            currentData.user_signatures = currentData.user_signatures || {};
            currentData.user_signatures[userId] = emailSignature;
            currentData.shared_mailboxes = sharedMailboxes;
            currentData.default_shared_mailbox = defaultSharedMailbox;

            const { error } = await supabase.from('clients').update({ data: currentData }).eq('id', clientId);
            if (error) throw error;
        } catch (e) {
            console.error("Fout bij opslaan handtekening", e);
            alert("Kon handtekening niet opslaan.");
        } finally {
            setIsSavingSignature(false);
        }
    };

    const handleCreateNew = () => {
        setAiPrompt('');
        setEditingTemplate({
            id: `tpl_${Date.now()}`,
            creatorId: userId,
            visibility: 'company',
            sharedWithIds: [],
            name: 'Nieuwe Template',
            subject: '',
            body: '',
            pipelineStatuses: []
        });
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Weet je zeker dat je deze template wilt verwijderen?")) return;
        const newTemplates = templates.filter(t => t.id !== id);
        await saveTemplatesToDb(newTemplates);
    };

    const handleGenerateAI = async () => {
        if (!editingTemplate) return;
        const topic = aiPrompt.trim();
        if (!topic) return alert("Vul eerst in waar de mail over moet gaan.");

        setIsGenerating(true);
        try {
            const promptStr = `Je bent Froukje, een professionele recruiter. Schrijf een email template voor het onderwerp: "${topic}". 
            
Lever ALTIJD een JSON string op met deze keys:
{"subject": "Het onderwerp", "body": "De body van de tekst."}

Gebruik geen placeholders voor de afzender of ontvanger in de body (alleen een open aanhef 'Beste kandidaat,' en afsluiting namens de recruiter).`;

            const response = await ai.models.generateContent({
                model: AI_PROXY_CHAT_MODEL,
                contents: promptStr,
                config: { responseMimeType: "application/json" }
            });

            if (response.text) {
                const parsed = JSON.parse(response.text);
                setEditingTemplate({
                    ...editingTemplate,
                    subject: parsed.subject || '',
                    body: parsed.body || ''
                });
            }
        } catch (e) {
            console.error("Generatie mislukt", e);
            alert("Genereren mislukt. Probeer het opnieuw.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSaveTemplate = async () => {
        if (!editingTemplate) return;
        if (!editingTemplate.name.trim()) return alert("Vul een naam in voor de template.");

        const isExisting = templates.some(t => t.id === editingTemplate.id);
        const newTemplates = isExisting
            ? templates.map(t => t.id === editingTemplate.id ? editingTemplate : t)
            : [...templates, editingTemplate];

        await saveTemplatesToDb(newTemplates);
    };

    if (isLoading) {
        return <div className="p-10 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>;
    }

    if (editingTemplate) {
        return (
            <div className="max-w-3xl space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <button onClick={() => { setEditingTemplate(null); setAiPrompt(''); }} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-2">
                            <ArrowLeft size={14} /> Terug naar overzicht
                        </button>
                        <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                            <Mail className="text-red-" size={20} /> Template Bewerken
                        </h2>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Template Naam</label>
                        <input
                            type="text"
                            value={editingTemplate.name}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                            placeholder="Bijv. Standaard Afwijzing"
                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-red-"
                        />
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex flex-col gap-3">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block">AI Assistent (Optioneel)</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={aiPrompt}
                                onChange={e => setAiPrompt(e.target.value)}
                                placeholder="Waar moet deze mail over gaan? (bijv. 'Korte afwijzing')"
                                className="flex-1 p-3 bg-purple-50/50 border border-purple-100 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-purple-300 placeholder:text-purple-300"
                            />
                            <button
                                onClick={handleGenerateAI}
                                disabled={isGenerating || !aiPrompt.trim()}
                                className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-purple-200 flex items-center gap-2 disabled:opacity-50 transition-colors shrink-0"
                            >
                                {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Genereer met AI
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">E-mail Onderwerp</label>
                        <input
                            type="text"
                            value={editingTemplate.subject}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                            placeholder="Onderwerp van de mail..."
                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-red-"
                        />
                    </div>

                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Gekoppelde Pipeline Statussen (Optioneel)</label>
                        <div className="flex flex-wrap gap-2">
                            {['Shortlist', 'Voorgesteld', 'Gesprek', 'Aanbod', 'Plaatsing', 'Rejected'].map(status => {
                                const currentStatuses = editingTemplate.pipelineStatuses || (editingTemplate.pipelineStatus ? [editingTemplate.pipelineStatus] : []);
                                const isSelected = currentStatuses.includes(status);
                                return (
                                    <label key={status} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                        <input 
                                            type="checkbox"
                                            className="hidden"
                                            checked={isSelected}
                                            onChange={(e) => {
                                                const newStatuses = e.target.checked ? [...currentStatuses, status] : currentStatuses.filter(s => s !== status);
                                                setEditingTemplate({ ...editingTemplate, pipelineStatuses: newStatuses });
                                            }}
                                        />
                                        {status === 'Rejected' ? 'Afgewezen' : status}
                                    </label>
                                );
                            })}
                        </div>
                        <p className="text-[10px] text-slate-400 font-medium mt-1.5">Koppel deze template aan een of meerdere fases, zodat hij sneller bovenaan staat in de mail-composer.</p>
                    </div>

                                        <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Zichtbaarheid / Delen</label>
                        <select
                            value={editingTemplate.visibility || 'company'}
                            onChange={(e) => {
                                const val = e.target.value as 'company' | 'private' | 'specific';
                                setEditingTemplate({ ...editingTemplate, visibility: val, sharedWithIds: val === 'specific' ? editingTemplate.sharedWithIds || [] : [] });
                            }}
                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-indigo-400"
                        >
                            <option value="company">Iedereen in het bedrijf (Standaard)</option>
                            <option value="private">Alleen mezelf</option>
                            <option value="specific">Specifieke personen</option>
                        </select>
                        {editingTemplate.visibility === 'specific' && (
                            <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Selecteer collega's</label>
                                {teamMembers.filter(m => m.id !== userId).map(member => (
                                    <label key={member.id} className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                            checked={(editingTemplate.sharedWithIds || []).includes(member.id)}
                                            onChange={(e) => {
                                                const currentIds = editingTemplate.sharedWithIds || [];
                                                setEditingTemplate({
                                                    ...editingTemplate,
                                                    sharedWithIds: e.target.checked ? [...currentIds, member.id] : currentIds.filter(id => id !== member.id)
                                                });
                                            }}
                                        />
                                        {member.name || 'Onbekende naam'}
                                    </label>
                                ))}
                                {teamMembers.filter(m => m.id !== userId).length === 0 && <span className="text-xs text-slate-500">Geen andere teamleden gevonden.</span>}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="flex justify-between items-end mb-1.5">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block">Bericht Body</label>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 cursor-pointer">
                                    <input type="checkbox" checked={showHtml} onChange={(e) => setShowHtml(e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-3 h-3" />
                                    Show HTML
                                </label>
                                <span className="text-[9px] font-bold text-slate-400 flex flex-wrap items-center gap-1 justify-end">Gebruik variabelen: <span className="text-purple-600 bg-purple-50 px-1 py-0.5 rounded">[NAAM]</span> <span className="text-purple-600 bg-purple-50 px-1 py-0.5 rounded">[FUNCTIE]</span> <span className="text-purple-600 bg-purple-50 px-1 py-0.5 rounded">[BEDRIJF]</span> <span className="text-purple-600 bg-purple-50 px-1 py-0.5 rounded">[VACATURE]</span> <span className="text-purple-600 bg-purple-50 px-1 py-0.5 rounded">[HANDTEKENING]</span></span>
                            </div>
                        </div>
                        {showHtml ? (
                            <textarea
                                value={editingTemplate.body}
                                onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
                                className="w-full h-64 p-4 text-xs font-mono bg-slate-900 text-green-400 rounded-xl border border-slate-200 outline-none resize-y"
                                placeholder="<p>HTML body hier...</p>"
                            />
                        ) : (
                            <RichTextEditor
                                value={editingTemplate.body}
                                onChange={(val) => setEditingTemplate({ ...editingTemplate, body: val })}
                                placeholder="Schrijf hier de mail inhoud..."
                            />
                        )}
                    </div>

                    <button
                        onClick={handleSaveTemplate}
                        disabled={isSaving}
                        className="w-full py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black flex items-center justify-center gap-2"
                    >
                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Template Opslaan
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                        <Mail className="text-red-" size={24} /> E-mail Templates
                    </h2>
                    <p className="text-xs text-slate-500 font-bold mt-1">Beheer herbruikbare bericht-templates gestuurd via de Talentpool.</p>
                </div>
                <button
                    onClick={handleCreateNew}
                    className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-md hover:bg-black transition-all"
                >
                    <Plus size={16} /> Nieuwe Template
                </button>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <PenTool size={18} className="text-slate-400" />
                    <div>
                        <h3 className="font-bold text-slate-900">Algemene E-mail Handtekening</h3>
                        <p className="text-xs text-slate-500 font-medium">Deze handtekening wordt automatisch onderaan alle uitgaande bulk-mails vanuit de Talentpool geplaatst.</p>
                    </div>
                </div>
                <RichTextEditor 
                    value={emailSignature}
                    onChange={(val) => setEmailSignature(val)}
                    placeholder="Met vriendelijke groet,&#10;&#10;Werftools Recruitment"
                />
                <div className="flex justify-end">
                    <button
                        onClick={handleSaveSignature}
                        disabled={isSavingSignature}
                        className="px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 flex items-center justify-center gap-2 transition-all"
                    >
                        {isSavingSignature ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Handtekening & Instellingen Opslaan
                    </button>
                </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                    <Mail size={18} className="text-slate-400" />
                    <div>
                        <h3 className="font-bold text-slate-900">Gedeelde Mailboxen</h3>
                        <p className="text-xs text-slate-500 font-medium">Stel gedeelde mailboxen (bijv. info@bedrijf.nl) in die recruiters kunnen gebruiken om e-mails vanuit te versturen. Je moet wel in M365 de 'Verzenden Als' of 'Verzenden Namens' rechten hebben op deze mailboxen.</p>
                    </div>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Gedeelde Mailboxen (komma gescheiden)</label>
                        <input
                            type="text"
                            value={sharedMailboxes}
                            onChange={(e) => setSharedMailboxes(e.target.value)}
                            placeholder="bijv. info@werfplaats.nl, vacatures@werfplaats.nl"
                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-indigo-400"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Standaard Afzender Mailbox</label>
                        <select
                            value={defaultSharedMailbox}
                            onChange={(e) => setDefaultSharedMailbox(e.target.value)}
                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-indigo-400"
                        >
                            <option value="">-- Gebruik mijn eigen mailbox --</option>
                            {sharedMailboxes.split(',').map(m => m.trim()).filter(Boolean).map(mailbox => (
                                <option key={mailbox} value={mailbox}>{mailbox}</option>
                            ))}
                        </select>
                        <p className="text-[10px] text-slate-400 font-medium mt-1">Deze mailbox is standaard geselecteerd in de e-mail composer als afzender.</p>
                    </div>
                </div>

                <div className="flex justify-end mt-4">
                    <button
                        onClick={handleSaveSignature}
                        disabled={isSavingSignature}
                        className="px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 flex items-center justify-center gap-2 transition-all"
                    >
                        {isSavingSignature ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Handtekening & Instellingen Opslaan
                    </button>
                </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">Mijn Templates ({templates.length})</h3>


            {templates.length === 0 ? (
                <div className="p-12 text-center bg-slate-50 rounded-2xl border border-slate-200 border-dashed">
                    <Mail className="mx-auto text-slate-300 mb-3" size={32} />
                    <p className="text-sm font-bold text-slate-500">Nog geen templates ingesteld.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {templates.map(tpl => (
                        <div key={tpl.id} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:border-red- transition-colors flex flex-col justify-between min-h-[160px]">
                            <div>
                                <h4 className="text-sm font-black text-slate-900 mb-1">{tpl.name}</h4>
                                {(() => {
                                    const statuses = tpl.pipelineStatuses || (tpl.pipelineStatus ? [tpl.pipelineStatus] : []);
                                    if (statuses.length === 0) return null;
                                    return (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                            {statuses.map(s => (
                                                <span key={s} className="inline-block px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-[8px] font-black uppercase tracking-widest rounded-md border border-indigo-100">
                                                    {s === 'Rejected' ? 'Afgewezen' : s}
                                                </span>
                                            ))}
                                        </div>
                                    );
                                })()}
                                <p className="text-xs font-bold text-slate-500 line-clamp-1 mb-2">Onderwerp: {tpl.subject || '(Geen onderwerp)'}</p>
                                <p className="text-[10px] text-slate-400 line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: tpl.body }} />
                            </div>
                            <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-slate-50">
                                <button
                                    onClick={() => setEditingTemplate(tpl)}
                                    className="p-2 text-slate-400 hover:text-red-0 bg-slate-50 hover:bg-red- rounded-lg flex items-center gap-2"
                                >
                                    <Edit2 size={14} /> <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Bewerk</span>
                                </button>
                                <button
                                    onClick={() => handleDelete(tpl.id)}
                                    className="p-2 text-slate-400 hover:text-red-500 bg-slate-50 hover:bg-red-50 rounded-lg"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            </div>
        </div>
    );
};
